// InteliChat #41 - the URGENCY BUCKETS (pure reactive, no side alerts).
//
// The single reactive derivation of the three never-miss buckets from
// NATIVE store truth. Kept free of the alert engine so BOTH the always-on
// HUD (via useNeverMiss, which adds the engine + prefs) and the Today hero
// can read the exact same urgent set without double-bootstrapping alerts.
//
// Membership (charlie's #41 spec, finalized): every row is action-needed
// (a chat you already replied to is NOT here). Then split by
// (customer here now?) x (waited how long?):
//   NEEDS YOU NOW = handled + here + awaiting + recent  (longest wait top)
//   NEW           = unclaimed + here + awaiting + recent (longest wait top)
//   MISSED        = waited past the window OR went offline (freshest top)
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import { useInteliConversation } from 'dashboard/intelichat/composables/useInteliConversation';
import { classifyBucket } from './urgency';

export function useUrgencyBuckets() {
  const store = useStore();
  const {
    openConversations,
    contactName,
    isUnassigned,
    isAwaitingReply,
    waitingSince,
    lastMessage,
    ensureConversationsLoaded,
  } = useInteliConversation();

  // ---- live clock: re-buckets chats as they age across the miss
  // threshold (a chat silently becomes MISSED at ~15 min) and drives the
  // rows' live wait timers. ----------
  const now = ref(Math.floor(Date.now() / 1000));
  let tick = null;

  // ---- visitor presence (online now) --------------------------------
  // Fed by the SAME ActionCable stream (intelichat:presence) into a local
  // reactive map; falls back to the native contacts store when a contact
  // is already loaded there (e.g. an open conversation).
  const presenceMap = ref({});
  const onPresence = contacts => {
    if (!contacts || typeof contacts !== 'object') return;
    presenceMap.value = { ...presenceMap.value, ...contacts };
  };

  const contactOnline = chat => {
    const cid = chat?.meta?.sender?.id;
    if (!cid) return false;
    if (presenceMap.value[cid]) return presenceMap.value[cid] === 'online';
    const contact = store.getters['contacts/getContact'](cid);
    return contact?.availability_status === 'online';
  };

  const lastLineOf = chat => {
    const m = lastMessage(chat);
    const c = m && m.content != null ? String(m.content) : '';
    return c.slice(0, 80) || 'New conversation';
  };

  const rowOf = (chat, extra = {}) => ({
    id: chat.id,
    name: contactName(chat),
    lastLine: lastLineOf(chat),
    waitingSince: waitingSince(chat),
    online: contactOnline(chat),
    contactId: chat?.meta?.sender?.id || null,
    ...extra,
  });

  // ---- single-owner classification (pure decision in ./urgency) ------
  const bucketOf = chat =>
    classifyBucket(
      {
        awaitingReply: isAwaitingReply(chat),
        waitingSince: waitingSince(chat),
        online: contactOnline(chat),
        unassigned: isUnassigned(chat),
      },
      now.value
    );

  // longest-waiting first (oldest waiting_since = smallest ts) for the
  // act-now buckets; most-recently-missed first for MISSED.
  const byLongestWait = (a, b) => (a.waitingSince || 0) - (b.waitingSince || 0);
  const byFreshestMiss = (a, b) => (b.waitingSince || 0) - (a.waitingSince || 0);

  const classified = computed(() =>
    openConversations.value.map(c => ({ chat: c, bucket: bucketOf(c) }))
  );

  const needsYouNow = computed(() =>
    classified.value
      .filter(x => x.bucket === 'needsYouNow')
      .map(x => rowOf(x.chat, { bucket: 'needsYouNow' }))
      .sort(byLongestWait)
  );

  const newIncoming = computed(() =>
    classified.value
      .filter(x => x.bucket === 'new')
      .map(x => rowOf(x.chat, { bucket: 'new' }))
      .sort(byLongestWait)
  );

  const missed = computed(() =>
    classified.value
      .filter(x => x.bucket === 'missed')
      .map(x => rowOf(x.chat, { bucket: 'missed' }))
      .sort(byFreshestMiss)
  );

  // the act-now set for the Today hero + the rail total badge.
  const actNow = computed(() =>
    [...needsYouNow.value, ...newIncoming.value].sort(byLongestWait)
  );
  const topUrgent = computed(() => actNow.value[0] || null);

  const needsYouNowCount = computed(() => needsYouNow.value.length);
  const newCount = computed(() => newIncoming.value.length);
  const missedCount = computed(() => missed.value.length);
  const actNowCount = computed(() => needsYouNowCount.value + newCount.value);

  onMounted(() => {
    emitter.on('intelichat:presence', onPresence);
    ensureConversationsLoaded();
    tick = setInterval(() => {
      now.value = Math.floor(Date.now() / 1000);
    }, 1000);
  });
  onBeforeUnmount(() => {
    emitter.off('intelichat:presence', onPresence);
    if (tick) clearInterval(tick);
  });

  return {
    now,
    contactOnline,
    needsYouNow,
    newIncoming,
    missed,
    needsYouNowCount,
    newCount,
    missedCount,
    actNow,
    topUrgent,
    actNowCount,
  };
}
